import clsx from "clsx";

import styles from "./bar-chart.module.css";

export interface Bar {
  /** Stable key, and what `data-bar` carries for tests. */
  key: string;
  /** The category under the bar, pre-formatted, e.g. `"8a"`. */
  label: string;
  value: number;
  /**
   * A bar still filling, such as the current hour. Drawn hatched, so a partial
   * period is not read as a low one.
   */
  partial?: boolean;
  /** Emphasises the category label, e.g. the current period. */
  current?: boolean;
}

export interface BarChartProps {
  bars: ReadonlyArray<Bar>;
  /** A horizontal line across the plot, e.g. the average. */
  reference?: { value: number; label: string };
  /** Formats the figure above each bar. Defaults to the bare number. */
  formatValue?: (value: number) => string;
  /** The chart's accessible name. */
  "aria-label": string;
  "data-test-role"?: string;
}

/**
 * A column chart: one bar per category, drawn with CSS rather than a charting
 * dependency, because the one shape it has to draw is a handful of labelled
 * columns and a reference line.
 *
 * The scale runs from zero to the larger of the tallest bar and the reference,
 * so the reference line is always inside the plot. Bars are percentages of it,
 * which is what lets the chart fill whatever width it is given.
 */
function BarChart({
  bars,
  reference,
  formatValue = String,
  "aria-label": ariaLabel,
  "data-test-role": testRole,
}: BarChartProps) {
  const max = Math.max(1, reference?.value ?? 0, ...bars.map((b) => b.value));

  return (
    <figure
      className={styles.chart}
      aria-label={ariaLabel}
      data-test-role={testRole}
    >
      <div className={styles.plot} role="list">
        {reference !== undefined && (
          <div
            className={styles.reference}
            style={{ bottom: `${(reference.value / max) * 100}%` }}
            data-test-role="bar-chart-reference"
            title={reference.label}
          />
        )}
        {bars.map((bar) => (
          <div
            key={bar.key}
            className={styles.column}
            role="listitem"
            data-bar={bar.key}
            aria-label={`${bar.label}: ${formatValue(bar.value)}`}
          >
            <span className={styles.value}>{formatValue(bar.value)}</span>
            <div
              className={clsx(styles.bar, { [styles.partial]: bar.partial })}
              style={{ height: `${(bar.value / max) * 100}%` }}
            />
          </div>
        ))}
      </div>
      <div className={styles.axis}>
        {bars.map((bar) => (
          <span
            key={bar.key}
            className={clsx(styles.category, { [styles.current]: bar.current })}
          >
            {bar.label}
          </span>
        ))}
      </div>
    </figure>
  );
}

export default BarChart;
